/**
 * Analytics page: first product surface for compliance intelligence. Brings together
 * key trends (momentum, audit readiness, compliance score), active insights,
 * snapshot comparison, and risk cluster. Brand-aligned, operator-friendly, non-punitive.
 * Uses existing analytics API only (no new backend).
 */
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import axios from 'axios';
import posthog from 'posthog-js';
import API_URL from '../../config';
import { Button } from '../ui';
import { getSubscriptionTier, mergeSubscriptionTier, readUserFromStorage } from '../../utils/subscription';
import { getSnapshots, getInsights, getSnapshotComparison } from '../../api/analytics';
import ComplianceMomentumCard from './ComplianceMomentumCard';
import AuditReadinessTrendCard from './AuditReadinessTrendCard';
import ComplianceTrendCard from './ComplianceTrendCard';
import ActiveInsightsPanel from './ActiveInsightsPanel';
import SnapshotComparisonModule from './SnapshotComparisonModule';
import RiskClusterPanel from './RiskClusterPanel';
import WhatMovedTheNeedle from './WhatMovedTheNeedle';
import AnalyticsDriIntelligenceHeader from './AnalyticsDriIntelligenceHeader';
import AnalyticsRynnPrompt from './AnalyticsRynnPrompt';
import { BarChart3, Download, Loader2 } from 'lucide-react';

const COMPARISON_WINDOWS = [7, 30, 90];

export default function AnalyticsPage() {
  const [snapshots, setSnapshots] = useState([]);
  const [insights, setInsights] = useState([]);
  const [comparison, setComparison] = useState(null);
  const [comparisonWindow, setComparisonWindow] = useState(30);
  const comparisonWindowRef = useRef(30);
  comparisonWindowRef.current = comparisonWindow;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [driScore, setDriScore] = useState(null);
  const [driLoading, setDriLoading] = useState(true);
  const [profileUser, setProfileUser] = useState(null);
  const [fleetReportDownloading, setFleetReportDownloading] = useState(false);
  const [fleetReportError, setFleetReportError] = useState('');

  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
  const auth = useMemo(
    () => (token ? { headers: { Authorization: `Bearer ${token}` } } : null),
    [token],
  );

  useEffect(() => {
    if (!auth) {
      setDriScore(null);
      setDriLoading(false);
      return;
    }
    setDriLoading(true);
    axios
      .get(`${API_URL}/api/dri/score`, auth)
      .then((r) => r.data?.totalScore ?? null)
      .then(setDriScore)
      .catch(() => setDriScore(null))
      .finally(() => setDriLoading(false));
  }, [auth]);

  useEffect(() => {
    if (!auth) {
      setProfileUser(null);
      return;
    }
    axios
      .get(`${API_URL}/api/auth/profile`, auth)
      .then((r) => {
        if (r.data) setProfileUser(mergeSubscriptionTier(r.data));
      })
      .catch(() => {
        const u = readUserFromStorage();
        setProfileUser(u ? mergeSubscriptionTier(u) : null);
      });
  }, [auth]);

  const canDownloadFleetReport = useMemo(() => {
    const t = getSubscriptionTier(profileUser);
    return t === 'pro' || t === 'fleet';
  }, [profileUser]);

  const handleDownloadFleetReport = useCallback(async () => {
    const t = localStorage.getItem('token');
    if (!t) return;
    setFleetReportError('');
    setFleetReportDownloading(true);
    try {
      const res = await fetch(`${API_URL}/api/reports/fleet`, {
        headers: { Authorization: `Bearer ${t}` },
      });
      if (res.status === 403) {
        let msg = 'Fleet Excel reports require a Pro or Fleet plan. Upgrade on the Pricing page to download this export.';
        try {
          const body = await res.json();
          if (typeof body?.message === 'string' && body.message.trim()) {
            msg = body.message.trim();
          }
        } catch {
          /* use default */
        }
        setFleetReportError(msg);
        return;
      }
      if (!res.ok) {
        setFleetReportError('Could not download report. Please try again.');
        return;
      }
      const blob = await res.blob();
      const date = new Date().toISOString().slice(0, 10);
      const name = `sovrynn-fleet-report-${date}.xlsx`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
      setFleetReportError('Could not download report. Please try again.');
    } finally {
      setFleetReportDownloading(false);
    }
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    const to = new Date();
    const from90 = new Date(to);
    from90.setDate(from90.getDate() - 90);
    const windowForCompare = comparisonWindowRef.current;
    try {
      const [snap90, insightsRes, compareRes] = await Promise.all([
        getSnapshots('daily_compliance', from90, to),
        getInsights({ activeOnly: true, limit: 50 }),
        getSnapshotComparison(windowForCompare).catch(() => null),
      ]);
      setSnapshots(Array.isArray(snap90) ? snap90 : []);
      setInsights(Array.isArray(insightsRes) ? insightsRes : []);
      setComparison(compareRes || null);
    } catch (err) {
      console.error('Analytics fetch error:', err);
      setError('Unable to load analytics. Try again in a moment.');
      setSnapshots([]);
      setInsights([]);
      setComparison(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleComparisonWindowChange = useCallback((windowDays) => {
    setComparisonWindow(windowDays);
    comparisonWindowRef.current = windowDays;
    getSnapshotComparison(windowDays).then(setComparison).catch(() => setComparison(null));
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    posthog.capture('analytics_viewed');
  }, []);

  const handleAcknowledge = useCallback(() => {
    getInsights({ activeOnly: true, limit: 50 }).then(setInsights);
  }, []);

  return (
    <div className="analytics-page page-container">
      <header className="ui-page-header">
        <div>
          <h1 className="ui-page-header__title">Analytics</h1>
          <p className="ui-page-header__subtitle">
            Your compliance intelligence — trends, scores, and what changed.
          </p>
        </div>
        <div className="ui-page-header__actions">
          {canDownloadFleetReport ? (
            <Button
              variant="secondary"
              type="button"
              icon={fleetReportDownloading ? Loader2 : Download}
              disabled={fleetReportDownloading}
              onClick={handleDownloadFleetReport}
              className={fleetReportDownloading ? 'analytics-fleet-report-btn--loading' : ''}
            >
              {fleetReportDownloading ? 'Downloading…' : 'Download Fleet Report'}
            </Button>
          ) : null}
          <Button variant="ghost" onClick={fetchData} icon={BarChart3}>
            Refresh
          </Button>
        </div>
      </header>

      {fleetReportError ? (
        <p className="analytics-fleet-report-error" role="alert">
          {fleetReportError}
        </p>
      ) : null}

      {error && (
        <div className="analytics-error" role="alert">
          {error}
        </div>
      )}

      <section className="analytics-section analytics-dri-header-wrap" aria-label="DRI Score overview">
        <AnalyticsDriIntelligenceHeader driScore={driScore} loading={driLoading} />
      </section>

      <section className="analytics-section analytics-hero" aria-label="Key trends">
        <div className="analytics-hero-grid">
          <ComplianceMomentumCard snapshots={snapshots} loading={loading} />
          <AuditReadinessTrendCard snapshots={snapshots} loading={loading} />
          <ComplianceTrendCard snapshots={snapshots} loading={loading} />
        </div>
      </section>

      <section className="analytics-section analytics-comparison" aria-label="Snapshot comparison and what moved the needle">
        <div className="analytics-comparison-header">
          <div className="analytics-range-tabs-wrap">
            <span className="analytics-range-label">Compare:</span>
            <div className="analytics-range-tabs">
              {COMPARISON_WINDOWS.map((w) => (
                <button
                  key={w}
                  type="button"
                  className={comparisonWindow === w ? 'active' : ''}
                  onClick={() => handleComparisonWindowChange(w)}
                  aria-pressed={comparisonWindow === w}
                >
                  {w} days
                </button>
              ))}
            </div>
          </div>
          {comparison?.comparisonWindowDays && (
            <p className="analytics-comparison-caption">
              Comparing latest snapshot to {comparison.comparisonWindowDays} days ago.
            </p>
          )}
        </div>
        <SnapshotComparisonModule snapshots={snapshots} loading={loading} />
        <WhatMovedTheNeedle comparison={comparison} loading={loading} comparisonWindowDays={comparison?.comparisonWindowDays} />
      </section>

      <section className="analytics-section analytics-insights-risk" aria-label="Insights and risks">
        <div className="analytics-two-col">
          <ActiveInsightsPanel insights={insights} loading={loading} onAcknowledge={handleAcknowledge} />
          <RiskClusterPanel insights={insights} loading={loading} />
        </div>
        <AnalyticsRynnPrompt />
      </section>
    </div>
  );
}
